// CounterList — the "what's physically in front of me" ingredient list.
//
// Renders the live counter state derived by recipeComponents.js:
//   • built intermediates ("Wet mixture", "Batter") as a top cluster
//   • remaining raw ingredients grouped WET / DRY
//   • each raw row: tap the NAME to check it off the bowl, tap the
//     AMOUNT pill to change measurement units (opens the unit picker)
//
// As the cook completes combine-steps upstream, consumed ingredients
// drop out and intermediates appear — this component just renders
// whatever the derivation hands it, so the collapse is automatic.
//
// State-free and reusable: pass the derived components + callbacks.
//   onToggleAdded(idx)  — check/uncheck a row
//   amountFor(ing)      — display amount string (honors unit overrides)
//   onPickUnit(ing)     — open the measurement-unit picker for a row
// amountFor/onPickUnit are optional; without them the amount renders as
// plain (non-tappable) text.

import React from "react";
import { FONT, COLOR } from "../lib/tokens";
import { applyPreferredUnit, DISPLAY_CONTEXT } from "../lib/unitPrefs";

// Amount-pill colors. Dark green on a light teal pill — readable, on the
// cook/progress green theme, and NOT the old yellow-on-yellow.
const AMOUNT_FG = "#1F6B5E";

function CheckDot({ added }) {
  return (
    <span
      aria-hidden
      style={{
        width: 20, height: 20, borderRadius: 10, flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        border: `1.5px solid ${added ? COLOR.leaf : COLOR.border}`,
        background: added ? COLOR.leaf : "transparent",
        color: added ? "#FFFFFF" : "transparent",
        fontSize: 12, fontWeight: 800, lineHeight: 1,
        transition: "all 140ms ease",
      }}
    >
      ✓
    </span>
  );
}

function IngredientRow({ row, onToggle, amountFor, onPickUnit }) {
  const { ing, added } = row;
  const amount =
    (amountFor ? amountFor(ing) : applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK))
    || ing.amount || "—";
  const name = ing.item || ing.ingredientId || "ingredient";
  // Only canonical-tagged rows can have their units converted.
  const canPickUnit = !!ing.ingredientId && typeof onPickUnit === "function";

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "7px 2px",
        opacity: added ? 0.5 : 1, transition: "opacity 140ms ease",
      }}
    >
      <button
        type="button"
        onClick={() => onToggle(row.idx)}
        className="mcm-focusable"
        style={{
          flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 12,
          background: "transparent", border: "none", padding: 0,
          textAlign: "left", cursor: "pointer", touchAction: "manipulation",
        }}
      >
        <CheckDot added={added} />
        <span
          style={{
            flex: 1, minWidth: 0,
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 16, lineHeight: 1.25, color: COLOR.ink,
            textDecoration: added ? "line-through" : "none",
          }}
        >
          {name}
        </span>
      </button>
      {canPickUnit ? (
        <button
          type="button"
          onClick={() => onPickUnit(ing)}
          className="mcm-focusable"
          style={{
            flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4,
            fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, color: AMOUNT_FG,
            background: COLOR.leafDeep, border: `1px solid ${COLOR.leafDim}`,
            borderRadius: 8, padding: "4px 9px",
            cursor: "pointer", touchAction: "manipulation",
            textDecoration: added ? "line-through" : "none",
          }}
        >
          <span>{amount}</span>
          <span style={{ fontSize: 8, opacity: 0.7 }}>▾</span>
        </button>
      ) : (
        <span
          style={{
            flexShrink: 0, fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
            color: AMOUNT_FG, padding: "4px 2px",
            textDecoration: added ? "line-through" : "none",
          }}
        >
          {amount}
        </span>
      )}
    </div>
  );
}

function GroupBlock({ label, rows, onToggle, amountFor, onPickUnit }) {
  if (!rows.length) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted,
          letterSpacing: "0.16em", textTransform: "uppercase",
          padding: "6px 2px 2px",
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {rows.map((row) => (
          <IngredientRow
            key={row.idx}
            row={row}
            onToggle={onToggle}
            amountFor={amountFor}
            onPickUnit={onPickUnit}
          />
        ))}
      </div>
    </div>
  );
}

// intermediates: [{ id, label }]  groups: { wet:[], dry:[] }
// singleSectionLabel: what to call the list when wet/dry ISN'T meaningful
// (only one of the two groups has anything) — "Ingredients" normally,
// "Rub" for a spice-rub recipe, etc. The wet/dry SPLIT only shows when the
// recipe genuinely has both a wet set and a dry set to combine.
export default function CounterList({
  intermediates = [],
  groups = { wet: [], dry: [] },
  // Ordered sections from deriveCounterComponents — the recipe's own
  // named mixtures ("Dry rub mixture") first, wet/dry leftovers last.
  // When any NAMED section exists it wins over the wet/dry force-fit;
  // absent (older callers), rendering falls back to groups.
  sections = null,
  onToggleAdded,
  amountFor,
  onPickUnit,
  singleSectionLabel = "Ingredients",
}) {
  const toggle = onToggleAdded || (() => {});
  const wet = groups.wet || [];
  const dry = groups.dry || [];
  const namedSections = Array.isArray(sections)
    ? sections.filter((s) => s?.id !== "wet" && s?.id !== "dry" && (s?.rows || []).length > 0)
    : [];
  const hasNamed = namedSections.length > 0;
  // Wet/dry labels only make sense when there's an actual wet mix AND an
  // actual dry mix to keep apart (baking-style). With just one group it's
  // not a "wet vs dry" situation — show a single list under a neutral /
  // context label (e.g. "Rub" for a meat rub) instead of a lone "DRY".
  const hasBoth = wet.length > 0 && dry.length > 0;
  const singleRows = [...wet, ...dry].sort((a, b) => (a?.idx ?? 0) - (b?.idx ?? 0));

  if (!intermediates.length && !wet.length && !dry.length && !hasNamed) {
    return (
      <div style={{ fontFamily: FONT.forma, fontSize: 13, fontStyle: "italic", color: COLOR.muted, padding: "8px 2px" }}>
        Everything's combined — nothing left on the counter.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {intermediates.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
          {intermediates.map((node) => (
            <span
              key={node.id}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
                fontSize: 14, color: COLOR.leaf,
                background: COLOR.leafDeep, border: `1px solid ${COLOR.leaf}55`,
                borderRadius: 999, padding: "6px 13px 6px 10px",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 16, height: 16, borderRadius: 8, flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: COLOR.leaf, color: "#FFFFFF", fontSize: 10, fontWeight: 800,
                }}
              >
                ✓
              </span>
              {node.label}
            </span>
          ))}
        </div>
      )}
      {hasNamed ? (
        <>
          {namedSections.map((s) => (
            <GroupBlock key={s.id} label={s.label} rows={s.rows} onToggle={toggle} amountFor={amountFor} onPickUnit={onPickUnit} />
          ))}
          {/* Whatever no named mixture claims. Wet/Dry only when both
              sides genuinely exist; a lone leftover side reads as
              "Everything else" — a lone DRY holding the chicken was
              exactly the nonsense the named sections replace. */}
          {hasBoth ? (
            <>
              <GroupBlock label="Wet" rows={wet} onToggle={toggle} amountFor={amountFor} onPickUnit={onPickUnit} />
              <GroupBlock label="Dry" rows={dry} onToggle={toggle} amountFor={amountFor} onPickUnit={onPickUnit} />
            </>
          ) : singleRows.length > 0 ? (
            <GroupBlock label="Everything else" rows={singleRows} onToggle={toggle} amountFor={amountFor} onPickUnit={onPickUnit} />
          ) : null}
        </>
      ) : hasBoth ? (
        <>
          <GroupBlock label="Wet" rows={wet} onToggle={toggle} amountFor={amountFor} onPickUnit={onPickUnit} />
          <GroupBlock label="Dry" rows={dry} onToggle={toggle} amountFor={amountFor} onPickUnit={onPickUnit} />
        </>
      ) : (
        <GroupBlock label={singleSectionLabel} rows={singleRows} onToggle={toggle} amountFor={amountFor} onPickUnit={onPickUnit} />
      )}
    </div>
  );
}
